import * as THREE from "three";
import type { MotionTuning, Vec3 } from "./card";
import type { Figure } from "./build";

// Procedural motion for a figure (decision 0030: the hero's extra effort goes into motion): a state machine (idle,
// walk, run, jump, fall, glide, land) choosing procedural poses, a gait whose feet plant where a ground probe says
// the ground is and stay there until they step, a head look-at inside its clamp, arms that show and reach their hand
// targets when acting, and the spring chains stepped after the pose. The game places the root (position and yaw)
// from its controller and feeds a `Locomotion` each frame; the motion poses the joints. No clips, no skinning.
//
// Gait (the procedural-locomotion and gait suggestions): one foot swings at a time; a planted foot steps when it falls
// half a step behind its home (its hip plus a velocity lead) or when its leg would stretch past `reach`; a swing lands
// where the hip will be at touchdown plus the lead, on the probed ground. Planted feet hold their anchor exactly, so
// planted drift comes only from the probe: switch the probe off (`MOTION_FAULTS.footProbeOff`) and planted feet ride
// the body's height instead of the ground's, and drift on any slope.

/** What the controller tells the figure each frame (the seam to the controller's state). */
export interface Locomotion {
  /** Horizontal speed, m/s. */
  speed: number;
  /** World velocity, m/s. */
  velocity: { x: number; y: number; z: number };
  grounded: boolean;
  /** A jump started (an edge, or held while rising). */
  jump: boolean;
  /** Gliding. */
  glide: boolean;
  /** Arms out (an action, a weapon drawn). */
  acting?: boolean;
  /** A world point to look at, or null to look ahead. */
  lookAt?: { x: number; y: number; z: number } | null;
}

/** The motion's states. */
export type MotionState = "idle" | "walk" | "run" | "jump" | "fall" | "glide" | "land";

/** The ground's height under (x, z), probing down from `fromY` (a ray through the game's collision), or null. */
export type GroundProbe = (x: number, z: number, fromY: number) => number | null;

/** Planted defects beside what they break: the drift and pose specs must fail with them on. */
export const MOTION_FAULTS = {
  /** Feet plant at the body's height instead of the probed ground's: planted feet drift on slopes and steps. */
  footProbeOff: false,
  /** The head turns to its target with no clamp. */
  lookUnclamped: false,
};

/** Planted-foot drift: how far a planted foot's rendered tip moved while it stayed planted. */
export interface DriftReport {
  /** Metres: the worst drift of any planted foot. */
  max: number;
  /** Plants seen and frames sampled while planted. */
  plants: number;
  samples: number;
}

/** What the motion reports each frame. */
export interface MotionReport {
  state: MotionState;
  /** Seconds in the state. */
  time: number;
  feet: { planted: boolean; plants: number; tip: Vec3 }[];
  /** The head's turn relative to the body (radians): yaw left-right, pitch up-down. */
  look: { yaw: number; pitch: number };
  drift: DriftReport;
  chains: Record<string, { speed: number; swing: number; tipOffset: number }>;
  /** State changes, oldest first (the last 32). */
  history: { from: MotionState; to: MotionState; at: number }[];
}

/** A figure's motion. */
export interface Motion {
  /** Pose the figure for this frame. The root must already be placed. */
  update(dt: number, input: Locomotion): void;
  state(): MotionState;
  report(): MotionReport;
  /** Clear the drift record (start a measurement). */
  resetDrift(): void;
  /** Plant the feet under the hips and settle the chains (after a teleport). */
  reset(): void;
}

interface Foot {
  leg: THREE.Object3D;
  mesh: THREE.Mesh | undefined;
  restLen: number;
  side: number;
  planted: boolean;
  swinging: boolean;
  t: number;
  from: THREE.Vector3;
  anchor: THREE.Vector3;
  pos: THREE.Vector3;
  plants: number;
  /** The drift tracker: which plant it is measuring and where that plant's tip started. */
  tracking: number;
  start: THREE.Vector3;
  tip: THREE.Vector3;
}

const UP = new THREE.Vector3(0, 1, 0);
const NEG_Y = new THREE.Vector3(0, -1, 0);
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const clamp = (x: number, a: number, b: number): number => Math.min(b, Math.max(a, x));
const approach = (cur: number, target: number, rate: number, dt: number): number => target + (cur - target) * Math.exp(-rate * dt);

/** Aim `joint`'s local -Y at the world point `target` (a rigid limb), returning the distance. */
function aim(joint: THREE.Object3D, target: THREE.Vector3): number {
  const parent = joint.parent!;
  const from = joint.getWorldPosition(_v2);
  const d = _v.copy(target).sub(from);
  const dist = d.length();
  if (dist < 1e-5) return 0;
  d.divideScalar(dist);
  const world = _q.setFromUnitVectors(NEG_Y, d);
  joint.quaternion.copy(parent.getWorldQuaternion(_q2).invert().multiply(world));
  return dist;
}

/**
 * The motion for a figure. `tuning` is the card's `motion`; `probe` finds the ground (a ray down through the game's
 * collision). Uses the joints `body`, `head`, `leg.l`/`leg.r` (their rest height is their length: legs reach the
 * ground at rest), `arm.l`/`arm.r` with `hand.l`/`hand.r`, whichever the card has.
 */
export function createMotion(fig: Figure, tuning: MotionTuning, probe: GroundProbe): Motion {
  const J = fig.joints;
  const body = J.get("body");
  const head = J.get("head");
  const bodyRest = body ? body.position.clone() : new THREE.Vector3();
  const feet: Foot[] = (["leg.l", "leg.r"] as const)
    .filter((id) => J.has(id))
    .map((id) => ({
      leg: J.get(id)!,
      mesh: fig.meshes.get(id),
      restLen: Math.max(0.05, fig.rest.get(id)!.y),
      side: fig.rest.get(id)!.x,
      planted: false,
      swinging: false,
      t: 0,
      from: new THREE.Vector3(),
      anchor: new THREE.Vector3(),
      pos: new THREE.Vector3(),
      plants: 0,
      tracking: -1,
      start: new THREE.Vector3(),
      tip: new THREE.Vector3(),
    }));
  const arms = (["r", "l"] as const)
    .filter((s) => J.has(`arm.${s}`) && J.has(`hand.${s}`))
    .map((s) => ({ side: s, arm: J.get(`arm.${s}`)!, hand: J.get(`hand.${s}`)!, mesh: fig.meshes.get(`arm.${s}`), len: fig.rest.get(`arm.${s}`)!.distanceTo(fig.rest.get(`hand.${s}`)!) }));
  const socketSide = fig.card.socket?.endsWith(".l") ? "l" : "r";

  let state: MotionState = "idle";
  let stateTime = 0;
  let clock = 0;
  let airTime = 0;
  let landLeft = 0;
  let lean = 0;
  let drop = 0;
  let yaw = 0;
  let pitch = 0;
  let primed = false;
  const lastRoot = new THREE.Vector3();
  let drift: DriftReport = { max: 0, plants: 0, samples: 0 };
  const history: MotionReport["history"] = [];

  const hipWorld = (f: Foot, out: THREE.Vector3): THREE.Vector3 => f.leg.getWorldPosition(out);
  const groundY = (x: number, z: number): number => {
    const y = probe(x, z, fig.root.position.y + 1.5);
    return y ?? fig.root.position.y;
  };
  const plantAt = (f: Foot, p: THREE.Vector3): void => {
    f.anchor.set(p.x, MOTION_FAULTS.footProbeOff ? fig.root.position.y : groundY(p.x, p.z), p.z);
    f.pos.copy(f.anchor);
    f.planted = true;
    f.swinging = false;
    f.t = 0;
    f.plants++;
  };
  const home = (f: Foot, vel: THREE.Vector3, ahead: number, out: THREE.Vector3): THREE.Vector3 => {
    hipWorld(f, out);
    out.addScaledVector(vel, ahead);
    return out;
  };

  const setState = (s: MotionState): void => {
    if (s === state) return;
    history.push({ from: state, to: s, at: clock });
    if (history.length > 32) history.shift();
    state = s;
    stateTime = 0;
  };

  const chooseState = (inp: Locomotion, dt: number): void => {
    if (!inp.grounded) {
      airTime += dt;
      if (inp.glide) setState("glide");
      else if (inp.velocity.y > 0 || (inp.jump && airTime < 0.3)) setState("jump");
      else if (airTime > 0.1 || state === "jump" || state === "glide") setState("fall");
      return;
    }
    const wasAir = state === "jump" || state === "fall" || state === "glide";
    if (wasAir && airTime > 0.08) {
      setState("land");
      landLeft = 0.18;
      for (const f of feet) plantAt(f, f.pos);
    }
    airTime = 0;
    if (state === "land" && landLeft > 0) {
      landLeft -= dt;
      return;
    }
    const sp = inp.speed;
    // hysteresis: start walking above `walk`, stop below 60 % of it; run above `run`, back to walk below 85 %
    if (state === "run") setState(sp < tuning.run * 0.85 ? (sp < tuning.walk * 0.6 ? "idle" : "walk") : "run");
    else if (state === "walk") setState(sp > tuning.run ? "run" : sp < tuning.walk * 0.6 ? "idle" : "walk");
    else setState(sp > tuning.run ? "run" : sp > tuning.walk ? "walk" : "idle");
  };

  const gait = (inp: Locomotion, dt: number): number => {
    const vel = _vel.set(inp.velocity.x, 0, inp.velocity.z);
    const sp = inp.speed;
    const k = clamp((sp - tuning.walk) / Math.max(1e-3, tuning.run - tuning.walk), 0, 1);
    const T = tuning.stepTime[0] + (tuning.stepTime[1] - tuning.stepTime[0]) * k;
    const threshold = Math.max(0.07, sp * T * 0.5);
    let lift = 0;
    // overstretched planted feet must step now; else the one farthest behind steps when none is swinging
    let anySwing = feet.some((f) => f.swinging);
    let due: Foot | null = null;
    let worst = threshold;
    for (const f of feet) {
      if (!f.planted) continue;
      if (MOTION_FAULTS.footProbeOff) f.anchor.y = fig.root.position.y;
      const hip = hipWorld(f, _hip);
      if (f.anchor.distanceTo(hip) > f.restLen * tuning.reach * 0.98) {
        startSwing(f);
        anySwing = true;
        continue;
      }
      const h = home(f, vel, tuning.lead, _home);
      const d = Math.hypot(f.anchor.x - h.x, f.anchor.z - h.z);
      if (d > worst) {
        worst = d;
        due = f;
      }
    }
    if (due && !anySwing) startSwing(due);
    for (const f of feet) {
      if (!f.swinging) continue;
      f.t = Math.min(1, f.t + dt / T);
      const land = home(f, vel, T * (1 - f.t) + T * 0.5 + tuning.lead, _home);
      land.y = MOTION_FAULTS.footProbeOff ? fig.root.position.y : groundY(land.x, land.z);
      const e = f.t * f.t * (3 - 2 * f.t);
      f.pos.copy(f.from).lerp(land, e).addScaledVector(UP, tuning.stepHeight * Math.sin(Math.PI * f.t) * clamp(sp / Math.max(1e-3, tuning.walk), 0.3, 1));
      lift = Math.max(lift, Math.sin(Math.PI * f.t));
      if (f.t >= 1) plantAt(f, land);
    }
    for (const f of feet) if (f.planted) f.pos.copy(f.anchor);
    return lift;
  };
  const startSwing = (f: Foot): void => {
    f.planted = false;
    f.swinging = true;
    f.t = 0;
    f.from.copy(f.pos);
  };

  const airLegs = (dt: number): void => {
    // legs tuck for a jump, hang for a fall, trail back in a glide (angles about x, root space)
    const angles: Record<string, [number, number]> = { jump: [-0.55, 0.35], fall: [-0.2, 0.2], glide: [0.55, 0.75] };
    const [a0, a1] = angles[state] ?? [0, 0];
    feet.forEach((f, i) => {
      f.planted = false;
      f.swinging = false;
      const a = i === 0 ? a0 : a1;
      const len = f.restLen * (state === "jump" ? 0.8 : 0.95);
      const local = _v.copy(f.leg.position);
      if (f.leg.parent !== fig.root) local.add(f.leg.parent!.position);
      local.x += state === "glide" ? f.side * 0.6 : 0;
      local.y -= Math.cos(a) * len;
      local.z -= Math.sin(a) * len;
      const target = fig.root.localToWorld(local.clone());
      f.pos.lerp(target, 1 - Math.exp(-14 * dt));
    });
  };

  const poseLegs = (): void => {
    for (const f of feet) {
      const dist = aim(f.leg, f.pos);
      if (f.mesh) f.mesh.scale.y = clamp(dist / f.restLen, 0.3, tuning.reach);
    }
  };

  const poseHead = (inp: Locomotion, dt: number): void => {
    if (!head) return;
    let ty = 0;
    let tp = 0;
    if (inp.lookAt) {
      head.parent!.updateWorldMatrix(true, false);
      const local = head.parent!.worldToLocal(_v.set(inp.lookAt.x, inp.lookAt.y, inp.lookAt.z)).sub(head.position);
      const flat = Math.hypot(local.x, local.z);
      if (flat > 0.3) {
        ty = Math.atan2(local.x, local.z);
        tp = Math.atan2(local.y, flat);
      }
    }
    if (!MOTION_FAULTS.lookUnclamped) {
      ty = clamp(ty, -tuning.look.yaw, tuning.look.yaw);
      tp = clamp(tp, -tuning.look.down, tuning.look.up);
    }
    yaw = approach(yaw, ty, tuning.look.rate, dt);
    pitch = approach(pitch, tp, tuning.look.rate, dt);
    head.rotation.set(-pitch, yaw, 0, "YXZ");
  };

  const poseArms = (inp: Locomotion): void => {
    const spec = fig.card.arms;
    const show = spec?.show === "always" || (spec?.show === "acting" && !!inp.acting);
    for (const a of arms) {
      const grip = a.side === socketSide ? fig.attachment?.grip : undefined;
      const restAt = spec?.rest?.[a.side];
      const at = grip?.at ?? (restAt ? (restAt.map((v) => v * fig.scale) as Vec3) : undefined);
      a.arm.visible = show && !!at;
      if (!a.arm.visible || !at) continue;
      // the hand target is given in the arm's parent's rest frame (model space as if that joint stood at rest)
      const parentRest = fig.rest.get(a.arm.parent!.name) ?? _zero;
      const target = a.arm.parent!.localToWorld(_v.set(at[0] - parentRest.x, at[1] - parentRest.y, at[2] - parentRest.z));
      const dist = aim(a.arm, target);
      if (a.mesh) a.mesh.scale.y = Math.max(0.2, dist / Math.max(1e-3, a.len));
      a.hand.position.set(0, -dist, 0);
      // the hand (and what it holds) turned to the grip's rotation in the body's frame
      const g = grip?.rot ?? [0, 0, 0];
      a.hand.quaternion.copy(a.arm.quaternion).invert().multiply(_q.setFromEuler(new THREE.Euler(g[0], g[1], g[2])));
    }
  };

  const measure = (): void => {
    fig.root.updateMatrixWorld(true);
    for (const f of feet) {
      // the rendered tip: the leg mesh's own matrix applied to its rest-length point
      if (f.mesh) f.tip.set(0, -f.restLen, 0).applyMatrix4(f.mesh.matrixWorld);
      else f.tip.copy(f.pos);
      if (!f.planted) continue;
      if (f.tracking !== f.plants) {
        f.tracking = f.plants;
        f.start.copy(f.tip);
        drift.plants++;
        continue;
      }
      drift.samples++;
      drift.max = Math.max(drift.max, f.tip.distanceTo(f.start));
    }
  };

  const reset = (): void => {
    fig.root.updateMatrixWorld(true);
    for (const f of feet) {
      const h = hipWorld(f, _hip);
      plantAt(f, h);
    }
    poseLegs();
    fig.root.updateMatrixWorld(true);
    for (const c of fig.chains.values()) c.reset();
    primed = true;
  };

  const motion: Motion = {
    update(dtIn, inp) {
      const dt = Math.min(0.1, Math.max(0, dtIn));
      clock += dt;
      stateTime += dt;
      // a teleport (the root moved over a metre since the last frame): plant afresh rather than drag the feet over
      if (!primed || lastRoot.distanceTo(fig.root.position) > 1) reset();
      lastRoot.copy(fig.root.position);
      chooseState(inp, dt);
      fig.root.updateMatrixWorld(true);

      // the body: lean, a dip on landing, bob with the swing, breath at rest, hover for floaters
      const sp = inp.speed;
      const leanTarget =
        state === "run" ? tuning.lean.run * clamp(sp / Math.max(tuning.run, 1e-3), 0, 1.3) :
        state === "walk" ? tuning.lean.run * 0.3 * clamp(sp / Math.max(tuning.run, 1e-3), 0, 1) :
        state === "glide" ? tuning.lean.glide :
        state === "jump" ? tuning.lean.jump :
        state === "fall" ? tuning.lean.jump * 0.5 : 0;
      lean = approach(lean, leanTarget, 8, dt);
      const dropTarget = state === "land" ? -0.06 : 0;
      drop = approach(drop, dropTarget, state === "land" ? 30 : 10, dt);
      let lift = 0;
      if (state === "jump" || state === "fall" || state === "glide") airLegs(dt);
      else lift = gait(inp, dt);
      const breath = state === "idle" ? tuning.breath * Math.sin(clock * 2.4) : 0;
      const hover = tuning.hover ? tuning.hover.height + tuning.hover.amp * Math.sin(clock * tuning.hover.rate) : 0;
      if (body) {
        body.position.copy(bodyRest).add(_v.set(0, drop + breath + hover + tuning.bob * lift * clamp(sp / Math.max(tuning.walk, 1e-3), 0, 1), 0));
        body.rotation.x = lean;
      }
      fig.root.updateMatrixWorld(true);
      poseLegs();
      poseHead(inp, dt);
      poseArms(inp);
      measure();
      for (const c of fig.chains.values()) c.update(dt);
    },
    state: () => state,
    report: () => {
      const chains: MotionReport["chains"] = {};
      for (const [id, c] of fig.chains) chains[id] = c.stats();
      const e = new THREE.Euler().setFromQuaternion(head?.quaternion ?? new THREE.Quaternion(), "YXZ");
      return {
        state,
        time: stateTime,
        feet: feet.map((f) => ({ planted: f.planted, plants: f.plants, tip: f.tip.toArray() as Vec3 })),
        look: { yaw: e.y, pitch: -e.x },
        drift: { ...drift },
        chains,
        history: [...history],
      };
    },
    resetDrift() {
      drift = { max: 0, plants: 0, samples: 0 };
      for (const f of feet) f.tracking = -1;
    },
    reset,
  };
  return motion;
}

const _vel = new THREE.Vector3();
const _hip = new THREE.Vector3();
const _home = new THREE.Vector3();
const _zero = new THREE.Vector3();
